import { useCrossFlow } from '@atlassiansox/cross-flow-support';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { type JiraTemplateIdType } from '../JiraTemplateId.types';
import { type TouchpointSourceType } from '../TouchpointSourceType';
import { trelloCrossFlowOpen } from '../TrelloCrossFlow';
import { useHasEligibleWorkspaces } from './runtimeEligibilityChecks/useHasEligibleWorkspaces';
import { useHasNoAvailableJiraSites } from './runtimeEligibilityChecks/useHasNoAvailableJiraSites';
import { useIsNewUser } from './runtimeEligibilityChecks/useIsNewUser';
import {
  isSourceTargetedByExperience,
  type CrossFlowExperience,
} from './isSourceTargetedByExperience';
import { type CrossFlowWorkspace } from './useEligibleWorkspacesForProvisioning';
import { useIsCrossFlowExperienceEligible } from './useIsCrossFlowExperienceEligible';

interface NewUserExperienceParams {
  source: TouchpointSourceType;
  boardId?: string;
  workspaceId?: string;
  hasValidAaSession: boolean;
  jiraTemplateId?: JiraTemplateIdType;
  workspaces: CrossFlowWorkspace[];
}

const EXPERIENCE_KEY = 'newUserExperience';

export const useCrossFlowForNewUserExperience = ({
  source,
  workspaceId,
  boardId,
  jiraTemplateId,
  hasValidAaSession,
  workspaces,
}: NewUserExperienceParams): {
  experience: CrossFlowExperience;
  isEligible: boolean;
  open: ReturnType<typeof trelloCrossFlowOpen>;
  getIsTargeted: () => boolean;
} => {
  const crossFlow = useCrossFlow();
  const { isEligible: isNewUserEligible } = useIsNewUser();
  const [
    isImmediateRuntimeEligibilityChecksPassed,
    setIsImmediateRuntimeEligibilityChecksPassed,
  ] = useState(false);

  const { isEligible: hasNoAvailableSites } = useHasNoAvailableJiraSites({
    doLoadAvailableSites: isImmediateRuntimeEligibilityChecksPassed,
  });

  const { isEligible: hasEligibleWorkspaces, eligibleWorkspaceOptions } =
    useHasEligibleWorkspaces({
      entitlementRequired: 'free',
      workspaceId,
      treatFreeTrialAsFree: true,
      workspaces,
    });

  const { isEligible: hasPaidWorkspaces } = useHasEligibleWorkspaces({
    workspaceId,
    entitlementRequired: 'standardOrPremium',
    treatFreeTrialAsFree: true,
    workspaces,
  });

  const eligibilityCheckResults = useMemo(() => {
    if (source === 'currentWorkspaceNavigationDrawer') {
      return [
        crossFlow.isEnabled,
        isNewUserEligible,
        hasEligibleWorkspaces,
        !!workspaceId,
      ];
    }
    return [
      crossFlow.isEnabled,
      isNewUserEligible,
      hasEligibleWorkspaces,
      !hasPaidWorkspaces,
    ];
  }, [
    source,
    crossFlow.isEnabled,
    isNewUserEligible,
    hasEligibleWorkspaces,
    workspaceId,
    hasPaidWorkspaces,
  ]);

  const experience = useIsCrossFlowExperienceEligible({
    experience: EXPERIENCE_KEY,
    source,
    eligibilityCheckResults,
    deferredEligibilityCheckResults: [hasNoAvailableSites],
  });

  useEffect(() => {
    // TODO ENGHEALTH-2180: baselined when the rule was enabled
    // oxlint-disable-next-line react/set-state-in-effect
    setIsImmediateRuntimeEligibilityChecksPassed(
      experience.isImmediateRuntimeEligibilityChecksPassed,
    );
  }, [experience.isImmediateRuntimeEligibilityChecksPassed]);

  const isSourceTargeted = isSourceTargetedByExperience(EXPERIENCE_KEY, source);
  const getIsTargeted = useCallback((): boolean => {
    // For experiences not gated by a TAP audience, use source-check as definition of targeted.
    return isSourceTargeted;
  }, [isSourceTargeted]);

  return useMemo(
    () => ({
      experience: experience.experience,
      isEligible: experience.isEligible,
      open: trelloCrossFlowOpen(
        crossFlow,
        source,
        eligibleWorkspaceOptions,
        hasValidAaSession,
        {
          workspaceId,
          boardId,
          jiraTemplateId,
          isNewTrelloUser: true,
          shouldSkipMarketingPage:
            source === 'currentWorkspaceNavigationDrawer',
        },
      ),
      getIsTargeted,
    }),
    [
      experience.experience,
      experience.isEligible,
      crossFlow,
      source,
      eligibleWorkspaceOptions,
      hasValidAaSession,
      workspaceId,
      boardId,
      jiraTemplateId,
      getIsTargeted,
    ],
  );
};
